import { Box, Button, Popover, SxProps, Theme } from '@mui/material';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import { HamburgerMenuIcon, MenuCloseIcon } from '../icon/Vectors';
import MobileNavMenu from '../mobileNavMenu/MobileNavMenu';
import logo from './../../assets/images/pge_spot_full_rgb_rev_md.png';
import Search from './../../assets/images/icons/search.svg';
import { useLanguage } from '../../utils/ReactIntlProvider';
import GaTracker from '../../utils/GaTracker/GaTracker';

type MenuItemProps = {
  to?: string;
  onClick?: (event: React.MouseEvent) => void;
  subMenu?: boolean;
  children: ReactNode;
  style?: React.CSSProperties;
};

function MenuItem({ to, onClick, subMenu, children, style: linkStyle }: MenuItemProps) {
  const location = useLocation();

  const commonStyle: SxProps<Theme> = {
    flex: '0 0 auto',
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    fontSize: '0.875rem',
    fontWeight: '500',
    textTransform: 'uppercase',
    cursor: 'pointer',
    color: (theme) => theme.palette.white[100],
  };

  const style: SxProps<Theme> = subMenu
    ? {
        ...commonStyle,
        px: '10px',
        py: '10px',
        width: '100%',
        borderLeft: '5px solid transparent',
        '&:hover': { borderLeft: (theme) => `5px solid ${theme.palette.orange[500]}` },
      }
    : {
        ...commonStyle,
        borderBottom: '4px solid transparent',
        '&:hover': { borderBottom: (theme) => `4px solid ${theme.palette.orange[500]}` },
      };

  const handleClick = (e: React.MouseEvent) => {
    if (to) {
      GaTracker.trackEvent({
        category: 'Header',
        action: to
          ? `navigate-from${
              location.pathname === '/' ? '-home' : location.pathname.replace('/', '-')
            }-to${to.replace('/', '-')}`
          : 'navigate',
        label: 'Header Navigation',
      });
    }
    onClick?.(e);
  };

  if (to) {
    const active = location.pathname === to;

    return (
      <Link to={to} onClick={handleClick} style={{ textDecoration: 'none', ...linkStyle }}>
        <Box
          component="span"
          sx={{
            ...style,
            borderBottom: subMenu ? 'none' : '4px solid',
            borderColor: active ? 'orange.500' : 'transparent',
            color: active && subMenu ? 'orange.500' : 'white.100',
          }}
        >
          {children}
        </Box>
      </Link>
    );
  } else {
    return (
      <Link
        to=""
        onClick={(event) => {
          event.preventDefault();
          onClick && handleClick(event);
        }}
        style={{ textDecoration: 'none', ...linkStyle }}
      >
        <Box sx={style}>{children}</Box>
      </Link>
    );
  }
}

type SubMenuProps = {
  title: string;
  children?: ReactNode;
  style?: React.CSSProperties;
};

const SubMenu = ({ title, children, style }: SubMenuProps) => {
  const buttonRef = useRef(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<Element | null>(null);

  const location = useLocation();

  const onClose = () => setMenuAnchorEl(null);

  useEffect(() => {
    // When the URL changes, the menu should close
    location.pathname && onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      <MenuItem to="" style={style}>
        <Button
          ref={buttonRef}
          onClick={() => {
            GaTracker.trackEvent({
              category: 'Button',
              action: `click-header-menu-${title.replace(' ', '-').toLowerCase()}`,
              label: 'Header',
            });
            setMenuAnchorEl(buttonRef.current);
          }}
          aria-controls="maps-nav-menu"
          aria-label={menuAnchorEl ? 'Close nav menu' : 'Open nav menu'}
          aria-expanded={menuAnchorEl ? 'true' : 'false'}
          disableRipple
          sx={{
            height: '100%',
            padding: 0,
            margin: 0,
            color: 'white.100',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&::after': {
              display: 'inline-block',
              marginLeft: '0.255em',
              verticalAlign: '0.255em',
              content: '""',
              borderTop: '0.3em solid',
              borderRight: '0.3em solid transparent',
              borderBottom: '0',
              borderLeft: '0.3em solid transparent',
            },
          }}
        >
          {title}
        </Button>
      </MenuItem>

      <Popover
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        id="maps-nav-menu"
        marginThreshold={0}
        componentsProps={{
          root: {
            'aria-hidden': true,
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 0,
            backgroundColor: 'blue.600',
          },
        }}
      >
        {children}
      </Popover>
    </>
  );
};

const MENU_ITEM_PADDING = { paddingTop: 24, paddingBottom: 24 } as React.CSSProperties;

export default function Header() {
  const { formatMessage } = useIntl();
  const { setLanguage } = useLanguage();

  const headerRef = useRef(null);

  const [mobileNavMenuAnchorEl, setMobileNavMenuAnchorEl] = useState<Element | null>(null);

  return (
    <Box
      component="header"
      ref={headerRef}
      position="relative"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      padding="0 1rem"
      height="80px"
      sx={{
        color: 'white',
        backgroundColor: 'blue.600',
      }}
    >
      <Box>
        <Link to="/">
          <img alt="PG&amp;E Logo" src={logo} width="56" />
        </Link>
      </Box>
      <Box
        component="nav"
        aria-label="primary"
        height="100%"
        display={{ xs: 'none', md: 'flex' }}
        flexDirection="row"
        gap={3}
        zIndex={100}
      >
        <MenuItem to="/" style={MENU_ITEM_PADDING}>
          {formatMessage({ id: 'nav.home' })}
        </MenuItem>
        <SubMenu title={formatMessage({ id: 'nav.incentives' })} style={MENU_ITEM_PADDING}>
          <MenuItem subMenu to="/income-qualified" style={MENU_ITEM_PADDING}>
            {formatMessage({ id: 'nav.incomeQualified' })}
          </MenuItem>
          <MenuItem subMenu to="/incentives" style={MENU_ITEM_PADDING}>
            {formatMessage({ id: 'nav.allOtherIncentives' })}
          </MenuItem>
        </SubMenu>
        <SubMenu title={formatMessage({ id: 'nav.vehiclesAndRates' })} style={MENU_ITEM_PADDING}>
          <MenuItem subMenu to="/vehicles" style={MENU_ITEM_PADDING}>
            {formatMessage({ id: 'nav.vehicles' })}
          </MenuItem>
          <MenuItem subMenu to="/rates" style={MENU_ITEM_PADDING}>
            {formatMessage({ id: 'nav.rates' })}
          </MenuItem>
        </SubMenu>
        <MenuItem to="/compare" style={MENU_ITEM_PADDING}>
          {formatMessage({ id: 'nav.compare' })}
        </MenuItem>
        <SubMenu title={formatMessage({ id: 'nav.maps' })} style={MENU_ITEM_PADDING}>
          <MenuItem to="/charging-stations" subMenu style={MENU_ITEM_PADDING}>
            {formatMessage({ id: 'nav.stations' })}
          </MenuItem>
          <MenuItem to="/charging-station-availability" subMenu style={MENU_ITEM_PADDING}>
            {formatMessage({ id: 'nav.availability' })}
          </MenuItem>
          <MenuItem to="/travel-range" subMenu style={MENU_ITEM_PADDING}>
            {formatMessage({ id: 'nav.range' })}
          </MenuItem>
          <MenuItem to="/map-a-route" subMenu style={MENU_ITEM_PADDING}>
            {formatMessage({ id: 'nav.route' })}
          </MenuItem>
          <MenuItem to="/dealers" subMenu style={MENU_ITEM_PADDING}>
            {formatMessage({ id: 'nav.dealer' })}
          </MenuItem>
        </SubMenu>
        <MenuItem to="/search" style={MENU_ITEM_PADDING}>
          <img src={Search} alt="Search" />
        </MenuItem>
        <SubMenu title={formatMessage({ id: 'languageName' })} style={MENU_ITEM_PADDING}>
          <MenuItem
            onClick={() => {
              GaTracker.trackEvent({
                category: 'Button',
                action: 'click-set-language-en',
                label: 'Language',
              });
              setLanguage('en');
            }}
            subMenu
          >
            English
          </MenuItem>
          <MenuItem
            onClick={() => {
              GaTracker.trackEvent({
                category: 'Button',
                action: 'click-set-language-es',
                label: 'Language',
              });
              setLanguage('es');
            }}
            subMenu
          >
            Español
          </MenuItem>
          <MenuItem
            onClick={() => {
              GaTracker.trackEvent({
                category: 'Button',
                action: 'click-set-language-zh',
                label: 'Language',
              });
              setLanguage('zh');
            }}
            subMenu
          >
            中文
          </MenuItem>
        </SubMenu>
      </Box>

      <Box display={{ xs: 'block', md: 'none' }}>
        <Button
          onClick={() => {
            GaTracker.trackEvent({
              category: 'Button',
              action: 'click-mobile-header-menu',
              label: 'Header',
            });
            setMobileNavMenuAnchorEl(headerRef.current);
          }}
          aria-controls="mobile-nav-menu"
          aria-label={mobileNavMenuAnchorEl ? 'Close nav menu' : 'Open nav menu'}
          aria-expanded={mobileNavMenuAnchorEl ? 'true' : 'false'}
        >
          <Box display={mobileNavMenuAnchorEl ? 'flex' : 'none'} alignItems="center">
            <MenuCloseIcon />
          </Box>
          <Box display={mobileNavMenuAnchorEl ? 'none' : 'flex'} alignItems="center">
            <HamburgerMenuIcon />
          </Box>
        </Button>

        <MobileNavMenu
          open={Boolean(mobileNavMenuAnchorEl)}
          anchorEl={mobileNavMenuAnchorEl}
          onClose={() => setMobileNavMenuAnchorEl(null)}
        />
      </Box>
    </Box>
  );
}
